import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";

import { UpdateRosterStatusModalStyles } from "./styled";

import Input from "components/Input/index";
import Button from "components/Buttons/index";

import { useRosterImportSlice } from "pages/TalentUploadRequest/slice";
import { selectRosterRequests } from "pages/TalentUploadRequest/slice/selector";
import Icon from "components/Icon/Icon";
import {
  X,
  BuildingOfficeIcon,
  UserCircleGearIcon,
  CheckIcon
} from "@phosphor-icons/react";

// Numeric fields in the modal
const NumericFieldsInModal = [
  "total_creators_uploaded",
  "new_creators_added_count",
  "updated_creators_count",
  "conflict_creators_count"
];

const UpdateRosterStatusModal = ({ show, onHide, rowData }) => {
  const dispatch = useDispatch();
  const { actions } = useRosterImportSlice();
  const { loading } = useSelector(selectRosterRequests);

  // Local state for form inputs
  const [formData, setFormData] = useState(
    NumericFieldsInModal.reduce((acc, key) => ({ ...acc, [key]: "" }), {})
  );

  // Initialize form inputs when rowData changes
  useEffect(() => {
    if (rowData) {
      setFormData(
        NumericFieldsInModal.reduce(
          (acc, key) => ({
            ...acc,
            [key]: rowData[key]?.toString() || ""
          }),
          {}
        )
      );
    } else {
      // Reset if rowData is null
      setFormData(
        NumericFieldsInModal.reduce((acc, key) => ({ ...acc, [key]: "" }), {})
      );
    }
  }, [rowData]);

  // Handle input changes; only digits allowed
  const handleChange = (e) => {
    const { id, value } = e.target;
    if (value === "" || /^\d+$/.test(value)) {
      setFormData((prev) => ({ ...prev, [id]: value }));
    }
  };

  // Check if any field changed
  const isChanged = rowData
    ? NumericFieldsInModal.some(
        (key) => rowData[key]?.toString() !== formData[key]
      )
    : false;

  // Handle form submission
  const handleSubmit = () => {
    if (!rowData) {
      onHide(); // Close modal if no rowData
      return;
    }

    // Prepare payload with only changed fields
    const changedData = NumericFieldsInModal.reduce((acc, key) => {
      const newValue = Number(formData[key]);
      if (rowData[key] !== newValue) {
        acc[key] = newValue;
      }
      return acc;
    }, {});

    if (Object.keys(changedData).length === 0) {
      onHide(); // Close if no changes
      return;
    }

    // Optimistic Redux update
    dispatch(
      actions.updateRosterRequestSuccess({
        ...rowData,
        ...changedData
      })
    );

    // Call API
    dispatch(
      actions.updateRosterRequest({
        id: rowData.id,
        ...changedData
      })
    );

    onHide(); // Ensure modal closes
  };

  return (
    <UpdateRosterStatusModalStyles
      show={show}
      onHide={onHide}
      className="sidebar-modal"
      backdrop
      keyboard
      unmountOnExit
    >
      <Modal.Header>
        <Modal.Title>Update Roster Status</Modal.Title>
        <div onClick={onHide} className="close-icon-wrapper">
          <Icon component={X} size={20} />
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="company-details-wrapper">
          <div className="brand-name">
            <div className="logo-text">
              <Icon component={BuildingOfficeIcon} color="#797979" /> Company
            </div>
            <p className="main-text">
              {rowData?.companies?.company_name || "-"}
            </p>
          </div>
          <div className="brand-name">
            <div className="logo-text">
              <Icon
                component={UserCircleGearIcon}
                color="#797979"
                format="outline"
                weight="regular"
              />
              Manager Name
            </div>
            <p className="main-text">{rowData?.manager?.name || "-"}</p>
          </div>
        </div>

        <div className="form-wrapper-styles">
          {NumericFieldsInModal.map((key) => (
            <Input
              key={key}
              id={key}
              label={key
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())}
              placeholder={`Enter ${key.replace(/_/g, " ")}`}
              value={formData[key]}
              onChange={handleChange}
              type="number"
              size="M"
            />
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          leftIcon={
            <Icon
              component={CheckIcon}
              format="outline"
              weight="regular"
              size={16}
            />
          }
          onClick={handleSubmit}
          disabled={loading || !isChanged}
        >
          Mark as Completed & Notify User
        </Button>
        <span className="warning-text">
          *Once submitted, user will be notified via email
        </span>
      </Modal.Footer>
    </UpdateRosterStatusModalStyles>
  );
};

export default UpdateRosterStatusModal;
